import React from 'react';

/**
 * One row in the viewer's rail — collapsed by default (2026-08-26, Fredrik).
 *
 * The rail now holds everything that used to live in sections below the
 * document, and a 360px column cannot show a dozen full comments at once.
 * So a row is a HEADLINE until you open it: who, when, the badges you scan
 * for, and a clipped line of the quote and the comment. Clicking it opens the
 * detail AND scrolls the content to the passage it is about — one action,
 * because reading a comment and looking at what it refers to is one act.
 *
 * WHAT NEVER TRUNCATES: the author, the time and the badges. Those are what
 * a reader scans a list for — a clipped "Mentions you" is worse than no badge
 * at all. Only the quote and the body are clipped, and both are restored in
 * full the moment the row opens.
 */

/** Clip lengths. The quote is shorter on purpose: it is context for the
    comment, not the thing being said. */
export const QUOTE_PREVIEW_CHARS = 90;
export const BODY_PREVIEW_CHARS = 140;

export function clip(text: string, limit: number): string {
  const flat = (text || '').replace(/\s+/g, ' ').trim();
  return flat.length <= limit ? flat : `${flat.slice(0, limit - 1).trimEnd()}…`;
}

interface CommentRowProps {
  commentId: string;
  author: string;
  at?: string | null;
  /** The passage the comment is about, if it has one. */
  quote?: string | null;
  /** Plain text used for the collapsed preview. */
  body: string;
  /** Badges — severity, mention, resolved, disposition. Never clipped. */
  badges?: React.ReactNode;
  /** Left edge colour, carrying the same meaning as the marker's. */
  accent?: string;
  /** The reader is mentioned here. */
  forMe?: boolean;
  /**
   * This is the CURRENT comment — the one whose anchor is marked in the
   * content. 2026-08-27: it used to mean "the scroll spy's reading position",
   * which is a different thing the moment you click, and clicking is exactly
   * when a reader needs to see which row they landed on.
   */
  active?: boolean;
  /** The pointer is over this row, or over its anchor in the content. */
  hovered?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onHoverChange?: (hovered: boolean) => void;
  /** False when the comment has no anchor to scroll to — a comment written
      without selecting anything. The row still opens; it just says so. */
  hasAnchor?: boolean;
  children?: React.ReactNode;
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

const CommentRow: React.FC<CommentRowProps> = ({
  commentId, author, at, quote, body, badges, accent, forMe, active, hovered,
  expanded, onToggle, onHoverChange, hasAnchor = true, children
}) => (
  <div
    // The id openComment() targets. It moved here with the cards; a second
    // element carrying it anywhere on the page would silently win or lose the
    // getElementById race, which is why the sections below the document are
    // gone rather than merely hidden.
    id={`comment-${commentId}`}
    data-testid="rail-comment-row"
    data-comment-row={commentId}
    className={`rail-row${expanded ? ' is-expanded' : ''}${active ? ' is-active' : ''}${hovered ? ' is-hovered' : ''}${forMe ? ' mentions-you' : ''}`}
    style={accent ? { borderLeftColor: accent } : undefined}
    // Enter/leave, not over/out: this is one element the pointer is either
    // in or not, and over/out would fire on every child crossing.
    onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
    onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
  >
    <button
      type="button"
      className="rail-row-toggle"
      aria-expanded={expanded}
      onClick={onToggle}
      title={hasAnchor
        ? (expanded ? 'Close' : 'Open, and scroll the content to this comment')
        : 'Open — this comment was written without selecting a passage, so there is nothing to scroll to'}
    >
      <span className="rail-row-meta">
        <span className="rail-row-author">{author}</span>
        {at && <span className="rail-row-when">{formatWhen(at)}</span>}
        {badges}
        <span className="rail-row-chevron" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
      </span>
      {!expanded && quote && (
        <span className="rail-row-quote">“{clip(quote, QUOTE_PREVIEW_CHARS)}”</span>
      )}
      {!expanded && (
        <span className="rail-row-preview">{clip(body, BODY_PREVIEW_CHARS)}</span>
      )}
    </button>
    {expanded && <div className="rail-row-body">{children}</div>}
  </div>
);

export default CommentRow;
